import classNames from "classnames";
import { ScrollArea } from "components/ScrollArea/ScrollArea";
import { Button, Checkbox, DropdownMenu, Radio, Text } from "design_system";
import { DropdownMenuProps } from "design_system/components/DropdownMenu";
import { ReactElement, ReactNode } from "react";

// Props for the main dropdown container
type ComposedDropdownRootProps = DropdownMenuProps & {
  scrollable?: boolean;
  renderInPortal?: boolean;
};

// Props for section headers
type ComposedDropdownSectionHeaderProps = {
  title: string;
};

// Props for dropdown sections
type ComposedDropdownSectionProps = {
  children: ReactNode;
  showBorder?: boolean;
  scrollable?: boolean;
  className?: string;
};

// Props for individual dropdown items
export type ComposedDropdownItemProps = {
  title: string;
  disclaimer?: string;
  description?: string;
  icon?: string | ReactNode;
  rightIcon?: ReactNode;
  isDisabled?: boolean;
  onClick?: () => void;
  dataTestId?: string;
  variant?: "default" | "danger";
};

// Props for dropdown footer
type ComposedDropdownFooterProps = {
  children?: ReactNode;
  primaryAction?: {
    label?: string;
    icon?: ReactElement;
    onClick: () => void;
    disabled?: boolean;
  };
  secondaryAction?: {
    label?: string;
    icon?: ReactElement;
    onClick: () => void;
  };
  className?: string;
};

/**
 * Main dropdown container component
 * Wraps content in a DropdownMenu with optional scrolling
 *
 * @param props.children - The content of the dropdown
 * @param props.triggerRef - Ref to the trigger element
 * @param props.open - Whether dropdown is open
 * @param props.onOpenChange - Open/close handler
 * @param props.scrollable - Enable scrolling for long content
 * @param props.hasPadding - Add padding to content
 * @param props.renderInPortal - Whether to use a portal for rendering. Defaults to false.
 */
function ComposedDropdownRoot({
  children,
  triggerRef,
  open,
  onOpenChange,
  scrollable = false,
  hasPadding = false,
  mobileTitle,
  renderInPortal = false,
}: ComposedDropdownRootProps) {
  return (
    <DropdownMenu
      triggerRef={triggerRef}
      open={open}
      onOpenChange={onOpenChange}
      hasPadding={hasPadding}
      mobileTitle={mobileTitle}
      renderInPortal={renderInPortal}
    >
      <>
        {scrollable ? (
          <ScrollArea
            orientation="vertical"
            hideScrollBar
            className="flex flex-col sm:max-h-[40vh]"
          >
            {children}
          </ScrollArea>
        ) : (
          children
        )}
      </>
    </DropdownMenu>
  );
}

/**
 * Section component for grouping dropdown items
 * Can contain header and multiple items
 *
 * @param props.children - Section content (header + items)
 * @param props.showBorder - Show border below section
 * @param props.scrollable - Enable scrolling within section
 * @param props.className - Custom CSS classes
 */
function Section({
  children,
  showBorder = true,
  scrollable = false,
  className = "",
}: ComposedDropdownSectionProps) {
  const Wrapper = scrollable ? ScrollArea : "div";
  const wrapperProps = scrollable
    ? {
        orientation: "vertical" as const,
        className: classNames(
          "flex flex-col max-h-[200px]",
          showBorder && "border-b",
          className
        ),
        contentClassName: "p-2 sm:p-1",
      }
    : {
        className: classNames(
          "flex p-2 sm:p-1 flex-col",
          showBorder && "border-b",
          className
        ),
      };

  return <Wrapper {...wrapperProps}>{children}</Wrapper>;
}

/**
 * Individual dropdown item component
 * Can be clickable with selection indicators
 *
 * @param props.title - Main item text
 * @param props.disclaimer - Optional disclaimer text
 * @param props.description - Optional description text
 * @param props.icon - Item icon (string for CSS class, ReactNode for component)
 * @param props.rightIcon - Right icon (ReactNode)
 * @param props.isDisabled - Whether item is disabled
 * @param props.onClick - Click handler
 * @param props.dataTestId - Test ID for testing
 */
function Item({
  title,
  disclaimer,
  description,
  icon,
  rightIcon,
  isDisabled = false,
  onClick,
  dataTestId,
  variant = "default",
}: ComposedDropdownItemProps) {
  // Use button if clickable, div if not
  const Wrapper = onClick ? "button" : "div";
  const wrapperClasses = classNames(
    "flex w-full p-2 gap-3 items-center rounded-xl bg-bg-base hover:bg-bg-faint",
    !isDisabled && "cursor-pointer",
    isDisabled && "opacity-50 cursor-not-allowed"
  );

  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={!isDisabled ? onClick : undefined}
      data-testid={dataTestId}
      className={wrapperClasses}
    >
      {/* Render icon - string for CSS class, ReactNode for component */}
      {icon && typeof icon === "string" ? (
        <img alt={title} className={`flex-shrink-0 w-5 h-5 ${icon}`} />
      ) : (
        <span
          className={classNames(
            "flex-shrink-0",
            variant === "danger" && "text-alert-fg-base"
          )}
        >
          {icon}
        </span>
      )}

      {/* Item content */}
      <div className="flex-1 flex-col gap-0.5">
        <div className="flex gap-1 items-center">
          <Text
            size="sm"
            weight="medium"
            className={classNames(
              "line-clamp-2",
              variant === "danger" && "!text-alert-fg-base"
            )}
          >
            {title}
          </Text>
          {disclaimer && (
            <>
              <Text size="sm" theme="muted">
                ·
              </Text>
              <Text size="sm" weight="medium" theme="muted">
                {disclaimer}
              </Text>
            </>
          )}
        </div>
        {description && (
          <Text size="x-sm" theme="muted" className="line-clamp-2">
            {description}
          </Text>
        )}
      </div>

      {/* Icon or selection indicator */}
      {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
    </Wrapper>
  );
}

/**
 * Checkbox item component
 * Wrapper around Item that adds a checkbox selection indicator
 *
 * @param props.isActive - Whether item is selected
 */
function CheckboxItem({
  isActive = false,
  ...props
}: ComposedDropdownItemProps & {
  isActive: boolean;
}) {
  return (
    <Item
      {...props}
      rightIcon={
        <Checkbox
          onClick={(e) => e.stopPropagation()} // Prevent click from firing twice
          readOnly
          isChecked={isActive}
        />
      }
    />
  );
}

/**
 * Radio item component
 * Wrapper around Item that adds a radio selection indicator
 *
 * @param props.isActive - Whether item is selected
 */
function RadioItem({
  isActive = false,
  ...props
}: ComposedDropdownItemProps & {
  isActive: boolean;
}) {
  return (
    <Item {...props} rightIcon={<Radio readOnly isChecked={isActive} />} />
  );
}

/**
 * Section header component
 * Displays a title for a dropdown section
 *
 * @param props.title - Header text
 */
function SectionHeader({ title }: ComposedDropdownSectionHeaderProps) {
  return (
    <div className="flex items-center px-2 py-1">
      <Text size="x-sm" weight="medium" theme="muted">
        {title}
      </Text>
    </div>
  );
}

/**
 * Footer component for dropdown actions
 * Can contain primary/secondary buttons or custom content
 *
 * @param props.children - Custom footer content
 * @param props.primaryAction - Primary button configuration
 * @param props.secondaryAction - Secondary button configuration
 * @param props.className - Custom CSS classes
 */
function Footer({
  children,
  primaryAction,
  secondaryAction,
  className = "",
}: ComposedDropdownFooterProps) {
  // Don't render if no content or actions
  if (!children && !primaryAction && !secondaryAction) {
    return null;
  }

  return (
    <div className="flex sticky bottom-0 flex-col-reverse gap-2 p-3 border-t bg-bg-base">
      {children || (
        <>
          {/* Secondary action button (e.g., Cancel) */}
          {secondaryAction && (
            <Button
              variant="tertiary"
              size="sm"
              onClick={secondaryAction.onClick}
              data-testid="cancel-button"
              iconLeft={secondaryAction.icon}
            >
              {secondaryAction.label}
            </Button>
          )}
          {/* Primary action button (e.g., Save, Confirm) */}
          {primaryAction && (
            <Button
              variant="primary"
              size="sm"
              onClick={primaryAction.onClick}
              isDisabled={primaryAction.disabled}
              iconLeft={primaryAction.icon}
            >
              {primaryAction.label}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

// Compose all components into a single Dropdown object
const Dropdown = Object.assign(ComposedDropdownRoot, {
  Section, // Dropdown.Section
  SectionHeader, // Dropdown.SectionHeader
  Item, // Dropdown.Item
  CheckboxItem, // Dropdown.CheckboxItem
  RadioItem, // Dropdown.RadioItem
  Footer, // Dropdown.Footer
});

export default Dropdown;
export { Dropdown };
